import { useMemo } from 'react'
import { arrayRemove, arrayUnion, collection, query, where, doc } from 'firebase/firestore'
import type { DocumentReference, Query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { deleteDoc, updateDoc } from '../lib/firestore'
import { db, functions } from '../lib/firebase'
import { useFirestoreDoc, useFirestoreCollection } from './useFirestore'
import { useBlockedUsers } from '../contexts/BlockedUsersContext'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import {
  isRestrictedAudience, pregameGoingIds, savedPlaceKey,
  type SavedPlace, type SavedPlacesDoc, type SavePregameRequest, type SavePregameResult,
} from '@shared/pregames'
import type { Pregame, UserProfile } from '../types'
import { useUserProfiles } from './useUserProfiles'
import { fbt } from 'fbtee'

const callSavePregame = httpsCallable<SavePregameRequest, SavePregameResult>(functions, 'savePregame')

const pregameRef = (hostId: string, eventId: string) =>
  doc(db, 'pregames', `${hostId}_${eventId}`) as DocumentReference<Pregame>

export interface EventPregames {
  mine: Pregame | null
  /** Pre-games others host that the viewer is invited to, soonest first. */
  invited: Pregame[]
  loading: boolean
}

/** The viewer's own pre-game for an event plus the ones they're invited to. */
export function useEventPregames(eventId: string, uid: string | null): EventPregames {
  const mineRef = useMemo(() => (uid ? pregameRef(uid, eventId) : null), [uid, eventId])
  const invitedRef = useMemo(
    () => uid
      ? (query(
          collection(db, 'pregames'),
          where('eventId', '==', eventId),
          where('inviteeIds', 'array-contains', uid),
        ) as Query<Pregame>)
      : null,
    [uid, eventId],
  )
  const mine = useFirestoreDoc<Pregame>(mineRef)
  const invitedQ = useFirestoreCollection<Pregame>(invitedRef)
  const { isBlocked } = useBlockedUsers()

  const invited = useMemo(
    () => invitedQ.data.filter(p => !isBlocked(p.hostId)).sort(byWhen),
    [invitedQ.data, isBlocked],
  )
  return { mine: mine.data, invited, loading: mine.loading || invitedQ.loading }
}

export async function savePregame(request: SavePregameRequest): Promise<void> {
  await runMutation(fbt("Couldn't save your pre-game", 'Error toast when saving a pre-game fails'), async () => {
    const { data } = await callSavePregame(request)
    if (!data.saved) return
    const params = { event_id: request.eventId, audience: isRestrictedAudience(request) ? 'restricted' : 'all' } as const
    track(data.created ? 'pregame_created' : 'pregame_updated', params)
  })
}

export async function clearPregame(hostId: string, eventId: string): Promise<void> {
  await runMutation(fbt("Couldn't remove your pre-game", 'Error toast when removing a pre-game fails'), async () => {
    await deleteDoc(pregameRef(hostId, eventId))
    track('pregame_cleared', { event_id: eventId })
  })
}

const EMPTY_PLACES: SavedPlace[] = []

/** Places the viewer used for earlier pre-games, most recent first (savePregame keeps the list). */
export function useSavedPlaces(uid: string | null): SavedPlace[] {
  const ref = useMemo(
    () => (uid ? (doc(db, 'users', uid, 'private', 'places') as DocumentReference<SavedPlacesDoc>) : null),
    [uid],
  )
  return useFirestoreDoc<SavedPlacesDoc>(ref).data?.places ?? EMPTY_PLACES
}

export async function removeSavedPlace(uid: string, places: SavedPlace[], place: SavedPlace): Promise<void> {
  await runMutation(fbt("Couldn't remove the place", 'Error toast when removing a saved pre-game place fails'), async () => {
    const key = savedPlaceKey(place)
    await updateDoc(doc(db, 'users', uid, 'private', 'places'), { places: places.filter(p => savedPlaceKey(p) !== key) })
  })
}

const EMPTY_BY_EVENT = new Map<string, Pregame[]>()

/**
 * Every pre-game the viewer hosts or is invited to, by event: their own first,
 * then invitations by day and time. Home uses it to badge plans that have one.
 */
export function useMyPregames(uid: string | null): Map<string, Pregame[]> {
  const hostedRef = useMemo(
    () => (uid ? (query(collection(db, 'pregames'), where('hostId', '==', uid)) as Query<Pregame>) : null),
    [uid],
  )
  const invitedRef = useMemo(
    () => (uid ? (query(collection(db, 'pregames'), where('inviteeIds', 'array-contains', uid)) as Query<Pregame>) : null),
    [uid],
  )
  const hosted = useFirestoreCollection<Pregame>(hostedRef).data
  const invited = useFirestoreCollection<Pregame>(invitedRef).data
  const { isBlocked } = useBlockedUsers()

  return useMemo(() => {
    if (!hosted.length && !invited.length) return EMPTY_BY_EVENT
    const byEvent = new Map<string, Pregame[]>()
    const add = (p: Pregame) => byEvent.set(p.eventId, [...(byEvent.get(p.eventId) ?? []), p])
    hosted.forEach(add)
    invited.filter(p => !isBlocked(p.hostId)).sort(byWhen).forEach(add)
    return byEvent
  }, [hosted, invited, isBlocked])
}

/** `profiles` plus pre-game goers outside the viewer's crew (a host's other friends), so their rows have names. */
export function usePregameGoerProfiles(
  byEvent: Map<string, Pregame[]>,
  profiles: Record<string, UserProfile>,
): Record<string, UserProfile> {
  const missing = useMemo(
    () => [...new Set([...byEvent.values()].flat().flatMap(pregameGoingIds))].filter(uid => !profiles[uid]).sort(),
    [byEvent, profiles],
  )
  const extra = useUserProfiles(missing)
  return useMemo(() => (missing.length ? { ...extra, ...profiles } : profiles), [missing, extra, profiles])
}

function byWhen(a: Pregame, b: Pregame): number {
  return `${a.day ?? ''}${a.time ?? '99'}`.localeCompare(`${b.day ?? ''}${b.time ?? '99'}`)
}

export type PregameRsvp = 'going' | 'not_going'

export function pregameRsvpOf(pregame: Pick<Pregame, 'goingIds' | 'notGoingIds'>, uid: string | null): PregameRsvp | null {
  if (!uid) return null
  if (pregame.goingIds?.includes(uid)) return 'going'
  if (pregame.notGoingIds?.includes(uid)) return 'not_going'
  return null
}

/** An invitee's answer, or null to take it back; rules only let them move their own uid. */
export async function setPregameRsvp(pregame: Pregame, uid: string, status: PregameRsvp | null): Promise<void> {
  await runMutation(fbt("Couldn't save your answer", 'Error toast when answering a pre-game invite fails'), async () => {
    await updateDoc(pregameRef(pregame.hostId, pregame.eventId), {
      goingIds: status === 'going' ? arrayUnion(uid) : arrayRemove(uid),
      notGoingIds: status === 'not_going' ? arrayUnion(uid) : arrayRemove(uid),
    })
    track('pregame_rsvp_set', { event_id: pregame.eventId, status: status ?? 'none' })
  })
}
